import { useMutation } from 'react-query'
import { MaintenanceType } from 'src/enums'
import { ICustomerLocalities } from 'src/generated-types/customerLocalities'
import { IFacilityWithTypes } from 'src/generated-types/facility'
import { IMaintenance } from 'src/generated-types/IMaintenance'
import { IMaintenanceTypeCount } from 'src/generated-types/IMaintenanceTypesCount'
import {
  IDeviationCounts,
  IDocumentsCount,
  IExpiringComponentsCount,
  ILocality,
  ILocalityEnvironmentDetails,
  ILocalityGroup,
  ILocalityNameAndLogo,
} from '../generated-types'
import { ILocalityRequestDto } from '../generated-types/locality-request-dto'
import { IMainComponentStatus } from '../generated-types/main-component-status'
import { queryClient } from '../query-client'
import { localityQueryKeys } from '../query-keys'
import { IPaginatedResult } from './paginated-result'
import { useDelete, useGet, usePost, usePut } from './reactQuery'
import { requestProvider } from './requestProvider'

const baseUrl = (customerId: EntityId) => `customers/${customerId}/localities`

const cageStatusUrl = (customerId: EntityId, localityId: EntityId) => `${baseUrl(customerId)}/${localityId}/cage-status`

export const endpoints = {
  getLocalities(customerId: EntityId) {
    return `${baseUrl(customerId)}`
  },
  getLocalitiesWithInfoCount(customerId: EntityId) {
    return `${baseUrl(customerId)}/get-all-with-info-count`
  },
  getLocalitiesWithGroups(customerId: EntityId) {
    return `${baseUrl(customerId)}/with-groups`
  },
  getLocalitiesWithMinimalInfo(customerId: EntityId) {
    return `${baseUrl(customerId)}/minimal-info`
  },
  getGrouppedLocalitiesWithInfoCount(customerId: EntityId) {
    return `${baseUrl(customerId)}/get-grouped-localities`
  },
  getLocalitiesNameAndLogo(customerId: EntityId) {
    return `${baseUrl(customerId)}/get-names-and-logos`
  },
  getLocalitiesForDropDown(customerId: EntityId) {
    return `${baseUrl(customerId)}/names`
  },
  getLocalityWithActivityCounts(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId)}/${localityId}/activity-counts`
  },
  getLocalitiesWithInfo(customerId: EntityId) {
    return `${baseUrl(customerId)}?withInfo=true`
  },
  getCageStatus(customerId: EntityId, localityId: EntityId) {
    return `${cageStatusUrl(customerId, localityId)}`
  },
  getLocality(customerId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/${id}`
  },
  getWorkAssignmentCount(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId)}/${localityId}/getWorkAssignmentCount?mooringId=${mooringId}`
  },
  getLocalityDeviationCounts(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId)}/${localityId}/get-deviation-counts`
  },
  getLocalityEnvironmentDetails(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId)}/${localityId}/get-environment-details?mooringId=${mooringId}`
  },
  getExpiringComponents(customerId: EntityId, localityId: EntityId, mooringId: EntityId) {
    return `${baseUrl(customerId)}/${localityId}/get-expiring-components?mooringId=${mooringId}`
  },
  getDocumentsCount(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId)}/${localityId}/getDocumentsCount`
  },
  createLocality(customerId: EntityId) {
    return `${baseUrl(customerId)}`
  },
  updateLocality(customerId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/${id}`
  },
  deleteLocality(customerId: EntityId) {
    return `${baseUrl(customerId)}`
  },
  getLocalityGroups(customerId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/${id}/locality-groups`
  },
  getMaintenanceTypesCount(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId)}/${localityId}/maintenance-types-count`
  },
  getMaintenances(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId)}/${localityId}/maintenance`
  },
  getMaintenancesWithCounts(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId)}/${localityId}/maintenance-with-counts`
  },
  regenerateLocalityMap(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId)}/${localityId}/regenerate-map`
  },
  getLocalitiesMinimalData(customerId: EntityId) {
    return `${baseUrl(customerId)}/minimal`
  },
}

export const invalidateLocalityCache = {
  getDocumentsCount(customerId: EntityId, localityId: EntityId) {
    queryClient.invalidateQueries([endpoints.getDocumentsCount(customerId, localityId)])
  },
  getLocalities(customerId?: EntityId, queryParams?: ILocalityRequestDto) {
    queryClient.invalidateQueries(localityQueryKeys.GET_LOCALITIES(customerId, queryParams))
  },
  getLocality(customerId?: EntityId, id?: EntityId, mainComponentId?: EntityId) {
    queryClient.invalidateQueries(localityQueryKeys.GET_LOCALITY(customerId, id, { mainComponentId: mainComponentId }))
    queryClient.invalidateQueries(localityQueryKeys.GET_LOCALITY_WITH_ACTIVITY_COUNTS(customerId, id))
  },
  getLocalityGroups(customerId: EntityId, id: EntityId) {
    queryClient.invalidateQueries([endpoints.getLocalityGroups(customerId, id)])
  },
  getCageStatus(customerId?: EntityId, localityId?: EntityId, queryParams?: object) {
    queryClient.invalidateQueries(localityQueryKeys.GET_CAGE_STATUS(customerId, localityId, queryParams))
  },
  getLocalitiesWithInfoCount(customerId?: EntityId) {
    queryClient.invalidateQueries(localityQueryKeys.GET_LOCALITY_WITH_INFO_COUNT(customerId))
    queryClient.invalidateQueries(localityQueryKeys.GET_LOCALITIES_LOGO_NAME(customerId))
  },
  getLocalityDeviationCounts(customerId?: EntityId, localityId?: EntityId) {
    queryClient.invalidateQueries(localityQueryKeys.GET_LOCALITY_DEVIATION_COUNT(customerId, localityId))
    queryClient.invalidateQueries(localityQueryKeys.GET_LOCALITY_WITH_ACTIVITY_COUNTS(customerId, localityId))
  },
  getLocalityWithActivityCounts(customerId: EntityId, localityId: EntityId) {
    queryClient.invalidateQueries(localityQueryKeys.GET_LOCALITY_WITH_ACTIVITY_COUNTS(customerId, localityId))
  },
  getMaintenanceTypesCount(customerId: EntityId, localityId: EntityId) {
    queryClient.invalidateQueries(endpoints.getMaintenanceTypesCount(customerId, localityId))
    queryClient.invalidateQueries(endpoints.getMaintenancesWithCounts(customerId, localityId))
  },
  getMaintenances(customerId: EntityId, localityId: EntityId) {
    queryClient.invalidateQueries(endpoints.getMaintenances(customerId, localityId))
  },
  getGroupedLocalitiesWithInfoCount(customerId?: EntityId) {
    queryClient.invalidateQueries(localityQueryKeys.GET_GROUPED_LOCALITY_WITH_INFO_COUNT(customerId))
  },
  CustomerLocalitiesWithGroups(customerId: EntityId) {
    queryClient.invalidateQueries(endpoints.getLocalitiesWithGroups(customerId as EntityId))
  },
  getLocalitiesMinimalData(customerId: EntityId) {
    queryClient.invalidateQueries(endpoints.getLocalitiesWithMinimalInfo(customerId))
  },
  getLocalitiesWithMinimalInfo(customerId: EntityId) {
    queryClient.invalidateQueries(localityQueryKeys.GET_LOCALITIES_WITH_MINIMAL_INFO(customerId))
  },
}

export const patchCageStatusCache = (
  customerId: EntityId,
  localityId: EntityId,
  positionId: EntityId,
  changes: Partial<IMainComponentStatus>,
) => {
  queryClient.setQueriesData<IMainComponentStatus[] | undefined>(
    localityQueryKeys.GET_CAGE_STATUS(customerId, localityId),
    old => old?.map(item => (item.id === positionId ? { ...item, ...changes } : item)),
  )
}

export const useGetAllWithInfoCount = (customerId: EntityId) =>
  useGet<ILocality[]>(endpoints.getLocalitiesWithInfoCount(customerId), undefined, {
    queryKey: localityQueryKeys.GET_LOCALITY_WITH_INFO_COUNT(customerId),
  })

export const useGetGrouppedLocalities = (customerId: EntityId, config?: { enabled?: boolean }) =>
  useGet<ICustomerLocalities>(endpoints.getGrouppedLocalitiesWithInfoCount(customerId), undefined, {
    queryKey: localityQueryKeys.GET_GROUPED_LOCALITY_WITH_INFO_COUNT(customerId),
    ...config,
  })

export const useGetLocalities = (customerId: EntityId, queryParams?: ILocalityRequestDto) =>
  useGet<IPaginatedResult<ILocality>>(
    endpoints.getLocalities(customerId),
    { ...queryParams },
    { queryKey: localityQueryKeys.GET_LOCALITIES(customerId) },
  )

export const useGetCustomerLocalities = ({ clientId }) =>
  useGet<ILocality[]>(endpoints.getLocalitiesForDropDown(clientId))

export const useGetCustomerLocalitiesWithGroups = ({ clientId }) =>
  useGet<IFacilityWithTypes[]>(endpoints.getLocalitiesWithGroups(clientId))

export const useGetLocalitiesNameAndLogo = customerId =>
  useGet<ILocalityNameAndLogo[]>(
    endpoints.getLocalitiesNameAndLogo(customerId),
    {},
    { queryKey: localityQueryKeys.GET_LOCALITIES_LOGO_NAME(customerId) },
  )

export const useGetCageStatus = (
  customerId: EntityId,
  localityId: EntityId,
  queryParams?: { page?: number; pageSize?: number; isIgnoreQueryFilters?: boolean },
) =>
  useGet<IMainComponentStatus[]>(
    endpoints.getCageStatus(customerId, localityId),
    { ...queryParams },
    { queryKey: localityQueryKeys.GET_CAGE_STATUS(customerId, localityId, queryParams) },
  )

export const useGetLocality = (customerId: EntityId, id: EntityId, queryParams?: ILocalityRequestDto) =>
  useGet<ILocality>(
    endpoints.getLocality(customerId, id ?? 0),
    { ...queryParams },
    {
      enabled: !!id,
      queryKey: localityQueryKeys.GET_LOCALITY(customerId, id, { mainComponentId: queryParams?.mainComponentId }),
    },
  )

export const useGetLocalityWithActivityCounts = (customerId: EntityId, id: EntityId, config?: { enabled?: boolean }) =>
  useGet<ILocality>(
    endpoints.getLocalityWithActivityCounts(customerId, id),
    {},
    {
      queryKey: localityQueryKeys.GET_LOCALITY_WITH_ACTIVITY_COUNTS(customerId, id),
      ...config,
    },
  )

export const useAddLocality = (customerId: EntityId) =>
  usePost<ILocality, ILocality>(endpoints.createLocality(customerId))

export const useGetLocalityNames = (customerId: EntityId) =>
  useGet<ILocality[]>(endpoints.getLocalitiesForDropDown(customerId))

export const useUpdateLocality = (customerId: EntityId, id: EntityId) =>
  usePut<ILocality, ILocality>(endpoints.updateLocality(customerId, id))

export const useDeleteLocality = (customerId: EntityId) => useDelete<ILocality>(endpoints.deleteLocality(customerId))

export const useGetLocalityGroups = (customerId: EntityId, id: EntityId) =>
  useGet<ILocalityGroup[]>(endpoints.getLocalityGroups(customerId, id))

export const useAssignLocalityGroups = (customerId: EntityId, id: EntityId) =>
  usePost(endpoints.getLocalityGroups(customerId, id))

export const useGetWorkAssignmentCount = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  useGet<number>(endpoints.getWorkAssignmentCount(customerId, localityId, mooringId))

export const useGetLocalityDeviationCounts = (customerId: EntityId, localityId: EntityId) =>
  useGet<IDeviationCounts>(
    endpoints.getLocalityDeviationCounts(customerId, localityId),
    {},
    {
      queryKey: localityQueryKeys.GET_LOCALITY_DEVIATION_COUNT(customerId, localityId),
    },
  )

export const useGetLocalityEnvironmentDetails = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  useGet<ILocalityEnvironmentDetails>(
    endpoints.getLocalityEnvironmentDetails(customerId, localityId, mooringId),
    {},
    { enabled: mooringId != null },
  )

export const useGetExpiringComponents = (customerId: EntityId, localityId: EntityId, mooringId: EntityId) =>
  useGet<IExpiringComponentsCount[]>(endpoints.getExpiringComponents(customerId, localityId, mooringId))

export const useGetDocumentsCount = (customerId: EntityId, localityId: EntityId) =>
  useGet<IDocumentsCount>(endpoints.getDocumentsCount(customerId, localityId))

export const useGetMaintenanceTypesCount = (
  customerId: EntityId,
  localityId: EntityId,
  queryParams?: { maintenanceForWeek: boolean },
  page?: number,
  pageSize?: number,
) =>
  useGet<IMaintenanceTypeCount>(endpoints.getMaintenanceTypesCount(customerId, localityId), {
    ...queryParams,
    page,
    pageSize,
  })

export const useGetMaintenances = (
  customerId: EntityId,
  localityId: EntityId,
  page?: number,
  pageSize?: number,
  queryParams?: { maintenanceType?: MaintenanceType },
) =>
  useGet<IPaginatedResult<IMaintenance>>(endpoints.getMaintenances(customerId, localityId), {
    ...queryParams,
    page,
    pageSize,
  })

export interface IMaintenanceWithCounts {
  counts: IMaintenanceTypeCount
  maintenances: IPaginatedResult<IMaintenance>
}

export const useGetMaintenancesWithCounts = (
  customerId: EntityId,
  localityId: EntityId,
  page?: number,
  pageSize?: number,
  queryParams?: { maintenanceType?: MaintenanceType },
) =>
  useGet<IMaintenanceWithCounts>(endpoints.getMaintenancesWithCounts(customerId, localityId), {
    ...queryParams,
    page,
    pageSize,
  })

export const useRegenerateLocalityMapMutation = (customerId: EntityId) => {
  return useMutation((localityId: EntityId) =>
    requestProvider.post(endpoints.regenerateLocalityMap(customerId, localityId), {}),
  )
}

export const useGetLocalitiesWithMinimalInfo = (customerId: EntityId) =>
  useGet<IPaginatedResult<ILocality>>(endpoints.getLocalitiesWithMinimalInfo(customerId), undefined, {
    queryKey: localityQueryKeys.GET_LOCALITIES_WITH_MINIMAL_INFO(customerId),
  })

export const useGetLocalitiesMinimalData = (customerId: EntityId) =>
  useGet<ILocality[]>(endpoints.getLocalitiesMinimalData(customerId), undefined)
